import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Loader } from '@/components/Loader';
import { useAIBudgetStatus } from '@/hooks/useAIBudget';
import { AISpendLimitEditForm } from '@/sections/settings/components/forms/AISpendLimitEditForm';
import { SettingDisplayCard } from '@/sections/settings/components/SettingDisplayCard';
import { SettingEditableCard } from '@/sections/settings/components/SettingEditableCard';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';
import { formatUSD } from '@/utils/formatCurrency.util';

interface AISpendCardProps {
  email?: string;
}

const CONTENT_RIGHT_PADDING = 'pr-24';

/**
 * AI Spend section — visible to all users, editable by Praetorians only.
 * The card always renders the marked-up budget/spend values returned by
 * the GET hook (no client-side cost/markup math); the edit affordance is
 * gated to Praetorian users via `editable={isPraetorianUser}`. Customers
 * (non-Praetorian users) still see the Edit button, but rendered DISABLED
 * with a "Managed by Praetorian" tooltip on hover (`lockedTooltip`),
 * mirroring the read-only feature toggles in PraetorianFeaturesSection.
 * Mirrors the visual cadence of the sibling
 * toggle cards in PraetorianFeaturesSection: title + one-line description.
 * Adds a thin progress bar when a limit is configured so spend-vs-cap is
 * legible at a glance.
 *
 * Bifrost only meters usage against an attached Budget; when none is
 * configured, current_usage is $0 and the bar is omitted.
 */
export const AISpendCard: React.FC<AISpendCardProps> = ({ email }) => {
  const { isPraetorianUser } = useAuth();
  const { data, isLoading, isError } = useAIBudgetStatus({ email });

  if (isLoading) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-loading">
        <h4 className="font-bold">AI Spend</h4>
        <Loader isLoading type="spinner" />
      </SettingDisplayCard>
    );
  }

  if (isError) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-error">
        <h4 className="font-bold">AI Spend</h4>
        <span className="text-chariot-text-secondary text-xs">
          Failed to load AI spend.
        </span>
      </SettingDisplayCard>
    );
  }

  if (data === null) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-unprovisioned">
        <h4 className="font-bold">AI Spend</h4>
        <span className="text-chariot-text-secondary text-xs">
          Not provisioned yet.
        </span>
      </SettingDisplayCard>
    );
  }

  if (data === undefined) {
    return null;
  }

  const hasLimit = data.set;
  const percent =
    hasLimit && data.max_limit > 0
      ? Math.min(100, (data.current_usage / data.max_limit) * 100)
      : 0;
  const overLimit = hasLimit && data.current_usage > data.max_limit;

  return (
    <SettingEditableCard
      data-testid="ai-spend-card"
      editable={isPraetorianUser}
      lockedTooltip={isPraetorianUser ? undefined : 'Managed by Praetorian'}
      editConfig={{
        modalTitle: 'Set AI Spend Limit',
        formComponent: AISpendLimitEditForm,
        formProps: {
          email,
          currentMaxLimit: hasLimit ? data.max_limit : undefined,
        },
        modalSize: 'lg',
        strategy: 'local-state',
      }}
    >
      <div
        className={cn(
          'flex w-full flex-col items-start justify-between gap-6 sm:flex-row sm:items-center',
          CONTENT_RIGHT_PADDING
        )}
      >
        {/* LEFT: title + capped meter + percent caption */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h4 className="font-bold">AI Spend</h4>
          {hasLimit && (
            <div
              className="bg-chariot-background-secondary h-2 w-full max-w-xs overflow-hidden rounded-full"
              role="progressbar"
              aria-valuenow={Math.round(percent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Spend ${percent.toFixed(0)}% of monthly limit`}
              data-testid="ai-spend-progress"
            >
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-300 ease-out',
                  overLimit
                    ? 'bg-chariot-status-critical'
                    : 'bg-chariot-brand-primary'
                )}
                style={{ width: `${Math.max(2, percent)}%` }}
              />
            </div>
          )}
          {hasLimit &&
            (overLimit ? (
              <div
                className="text-chariot-text-red flex items-center gap-1 text-xs font-medium"
                data-testid="ai-spend-overlimit"
              >
                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                <span data-testid="ai-spend-percent">
                  {percent.toFixed(0)}% · Over limit
                </span>
              </div>
            ) : (
              <span
                className="text-chariot-text-secondary text-xs"
                data-testid="ai-spend-percent"
              >
                {percent.toFixed(0)}% used
              </span>
            ))}
          {!hasLimit && (
            <span className="text-chariot-text-secondary text-xs">
              No monthly limit
            </span>
          )}
        </div>

        {/* RIGHT: promoted spend figure + limit caption */}
        <div className="flex shrink-0 flex-col items-start gap-1 text-right tabular-nums sm:items-end">
          <span
            className={cn(
              'text-lg font-semibold tabular-nums',
              overLimit ? 'text-chariot-text-red' : 'text-chariot-text-primary'
            )}
            data-testid="ai-spend-usage-value"
          >
            {formatUSD(data.current_usage)}
          </span>
          <span
            className="text-chariot-text-secondary text-xs"
            data-testid="ai-spend-limit-value"
          >
            {hasLimit
              ? `of ${formatUSD(data.max_limit)} monthly`
              : 'no monthly limit'}
          </span>
        </div>
      </div>
    </SettingEditableCard>
  );
};
